import { useMemo } from 'react';
import './GalaxyBackground.css';

interface Star {
  x: number;
  y: number;
  size: number;
  delay: number;
  duration: number;
  baseOpacity: number;
}

/** Deterministic seeded RNG so the starfield is stable across renders. */
function mulberry32(seed: number) {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function buildStars(count: number): Star[] {
  const rng = mulberry32(2026);
  return Array.from({ length: count }, () => ({
    x: rng() * 100,
    y: rng() * 100,
    size: 0.5 + rng() * 2,
    delay: rng() * 5,
    duration: 2.5 + rng() * 4,
    baseOpacity: 0.25 + rng() * 0.7,
  }));
}

interface GalaxyBackgroundProps {
  starCount?: number;
}

export function GalaxyBackground({ starCount = 220 }: GalaxyBackgroundProps) {
  const stars = useMemo(() => buildStars(starCount), [starCount]);

  return (
    <div className="galaxy" aria-hidden>
      {/* Distant twinkling starfield */}
      <div className="galaxy__stars">
        {stars.map((s, i) => (
          <span
            key={i}
            className="galaxy__star"
            style={{
              left: `${s.x}%`,
              top: `${s.y}%`,
              width: `${s.size}px`,
              height: `${s.size}px`,
              opacity: s.baseOpacity,
              animationDelay: `${s.delay}s`,
              animationDuration: `${s.duration}s`,
            }}
          />
        ))}
      </div>

      {/* Rotating spiral galaxy disk */}
      <div className="galaxy__disk-wrap">
        <div className="galaxy__disk" />
        <div className="galaxy__disk galaxy__disk--counter" />
        <div className="galaxy__core" />
      </div>

      {/* Coloured nebula haze that slowly drifts */}
      <div className="galaxy__nebula" />

      {/* Occasional shooting stars */}
      <span className="galaxy__shoot galaxy__shoot--1" />
      <span className="galaxy__shoot galaxy__shoot--2" />
    </div>
  );
}
